/** @fileoverview Animated activity label and elapsed time for a running assistant turn. */

import { BrainIcon, CircleHelp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Shimmer } from '../ai-elements/shimmer';
import { formatDuration, getToolStatusPhrase, startWithCapital } from './toolActivity';
import WorkingMark from './WorkingMark';
import type { ActivityEntry } from './types';

const IDLE_WORDS = Object.freeze(['Thinking', 'Working it out', 'Putting it together']);
const IDLE_WORD_SECONDS = 4;
const MS_IN_SECOND = 1000;

interface LiveStatusProps {
  entries: ActivityEntry[];
  progressLabel?: string;
  isWriting?: boolean;
  isReasoning?: boolean;
  waitingForAnswer: boolean;
  isStreaming?: boolean;
  completedLabel?: 'Worked' | 'Thought';
  durationMs?: number;
  /** When the work began; defaults to the moment this line first appears. */
  startedAt?: number;
}

const getElapsedSeconds = (startedAt: number) =>
  Math.max(0, Math.floor((Date.now() - startedAt) / MS_IN_SECOND));

const getRunningEntry = (entries: ActivityEntry[]) =>
  [...entries]
    .reverse()
    .find((entry) =>
      entry.kind === 'tool' ? entry.status === 'running' : entry.phase === 'started'
    );

const isActiveEntry = (entry: ActivityEntry) =>
  entry.kind === 'tool' ? entry.status === 'running' : entry.phase === 'started';

const isActivityEntry = (entry: ActivityEntry) =>
  entry.kind === 'skill' || Boolean(entry.activityLabel || entry.intent);

const getActivityText = (entry: ActivityEntry) =>
  entry.kind === 'skill'
    ? `Reading ${entry.skillName ?? 'a skill'}`
    : entry.intent || entry.activityLabel;

type StatusLabel = { label: string; activityKey?: string };

/** Prefers active work and progress updates over generic idle labels. */
const getStatusLabel = ({
  activityEntry,
  runningEntry,
  isWriting,
  isReasoning,
  progressLabel,
  elapsedSeconds,
}: {
  activityEntry: ActivityEntry | undefined;
  runningEntry: ActivityEntry | undefined;
  isWriting: boolean;
  isReasoning: boolean;
  progressLabel?: string;
  elapsedSeconds: number;
}): StatusLabel => {
  if (activityEntry) {
    return {
      label: getActivityText(activityEntry) ?? getToolStatusPhrase(),
      activityKey: activityEntry.key,
    };
  }
  if (runningEntry) {
    return {
      label:
        runningEntry.kind === 'tool'
          ? getToolStatusPhrase()
          : `Reading ${runningEntry.skillName ?? 'a skill'}`,
    };
  }
  if (isWriting) return { label: 'Writing' };
  if (progressLabel) return { label: startWithCapital(progressLabel) };
  if (isReasoning) return { label: 'Thinking' };
  return { label: IDLE_WORDS[Math.floor(elapsedSeconds / IDLE_WORD_SECONDS) % IDLE_WORDS.length] };
};

/** Animated activity and timer, used as the collapsible reasoning section's trigger. */
export default function LiveStatus({
  entries,
  progressLabel,
  isWriting = false,
  isReasoning = false,
  waitingForAnswer,
  isStreaming = true,
  completedLabel = 'Thought',
  durationMs,
  startedAt: startedAtProp,
}: LiveStatusProps) {
  const startedAt = useRef(startedAtProp ?? Date.now());
  const wasStreaming = useRef(isStreaming);
  const [elapsedSeconds, setElapsedSeconds] = useState(() => getElapsedSeconds(startedAt.current));
  const [finishedDuration, setFinishedDuration] = useState<number>();

  useEffect(() => {
    if (!isStreaming) {
      if (wasStreaming.current) {
        if (durationMs === undefined) {
          setFinishedDuration(Math.ceil((Date.now() - startedAt.current) / MS_IN_SECOND));
        }
        wasStreaming.current = false;
      }
      return undefined;
    }
    if (!wasStreaming.current) {
      startedAt.current = Date.now();
      setElapsedSeconds(0);
      setFinishedDuration(undefined);
    }
    wasStreaming.current = true;
    const timer = window.setInterval(
      () => setElapsedSeconds(getElapsedSeconds(startedAt.current)),
      MS_IN_SECOND
    );
    return () => window.clearInterval(timer);
  }, [durationMs, isStreaming]);

  const completedDuration = durationMs === undefined ? finishedDuration : durationMs / MS_IN_SECOND;
  const runningEntry = getRunningEntry(entries);
  // Prefer still-running work that carries an activity label; the latest running entry
  // may be a tool without one, which would drop the label while it is still running.
  const activeActivityEntry = [...entries]
    .reverse()
    .find((entry) => isActiveEntry(entry) && isActivityEntry(entry));
  // A call that finished before its label was ever shown still gets the 1s minimum.
  // Only the newest label qualifies, so labels arriving meanwhile are skipped, not queued.
  const latestActivityTool = [...entries]
    .reverse()
    .find((entry) => entry.kind === 'tool' && Boolean(entry.activityLabel || entry.intent));
  const shownActivityKey = useRef<string | undefined>(undefined);
  const activityEntry =
    activeActivityEntry ??
    (latestActivityTool && latestActivityTool.key !== shownActivityKey.current
      ? latestActivityTool
      : undefined);
  const baseStatus: StatusLabel = waitingForAnswer
    ? { label: 'Waiting for your answer' }
    : isStreaming
    ? getStatusLabel({
        activityEntry,
        runningEntry,
        isWriting,
        isReasoning,
        progressLabel,
        elapsedSeconds,
      })
    : { label: `${completedLabel} for ${formatDuration(completedDuration)}` };
  const [shown, setShown] = useState<StatusLabel>(baseStatus);
  // The mount label counts as consumed even when no switch animation runs for it.
  if (shownActivityKey.current === undefined) shownActivityKey.current = baseStatus.activityKey;

  // Once an activity label is shown, keep it while the work it describes is still running.
  const shownEntry =
    shown.activityKey === undefined
      ? undefined
      : entries.find((entry) => entry.key === shown.activityKey);
  const pinnedEntry = shownEntry && isActiveEntry(shownEntry) ? shownEntry : undefined;
  const nextStatus: StatusLabel =
    pinnedEntry && isStreaming && !waitingForAnswer
      ? { label: getActivityText(pinnedEntry) ?? shown.label, activityKey: pinnedEntry.key }
      : baseStatus;
  const nextLabel = nextStatus.label;
  const nextActivityKey = nextStatus.activityKey;
  const shownAt = useRef(Date.now());

  useEffect(() => {
    if (nextLabel === shown.label && nextActivityKey === shown.activityKey) {
      return undefined;
    }

    // One second is a floor for an activity label, never a cap: while its work is
    // still running nextLabel stays stable, so no timeout fires at all. A label change
    // during the floor reuses the same shownAt deadline, so the pending switch jumps
    // straight to the newest label instead of queueing intermediate ones.
    const remaining =
      shown.activityKey === undefined ? 0 : MS_IN_SECOND - (Date.now() - shownAt.current);
    const showNextLabel = () => {
      shownAt.current = Date.now();
      if (nextActivityKey !== undefined) shownActivityKey.current = nextActivityKey;
      setShown({ label: nextLabel, activityKey: nextActivityKey });
    };
    if (remaining <= 0) {
      showNextLabel();
      return undefined;
    }

    const timeout = window.setTimeout(showNextLabel, remaining);
    return () => window.clearTimeout(timeout);
  }, [nextActivityKey, nextLabel, shown]);

  const showDuration = isStreaming && !waitingForAnswer;

  return (
    <span
      role="status"
      className="flex min-w-0 flex-1 items-center gap-2 text-sm text-app-text-muted"
    >
      {waitingForAnswer ? (
        <CircleHelp aria-hidden="true" className="size-4 shrink-0" />
      ) : isStreaming ? (
        <WorkingMark />
      ) : (
        <BrainIcon aria-hidden="true" className="size-4 shrink-0" />
      )}
      {isStreaming && !waitingForAnswer ? (
        <Shimmer as="span" className="min-w-0 truncate text-sm" key={shown.label}>
          {shown.label}
        </Shimmer>
      ) : (
        <span className="min-w-0 truncate">{shown.label}</span>
      )}
      {showDuration && (
        <span className="shrink-0 text-app-text-muted/70 tabular-nums" aria-hidden="true">
          · {elapsedSeconds}s
        </span>
      )}
    </span>
  );
}
